import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Wallet, Package, ArrowLeftRight, TrendingUp, Search, Filter,
  Loader2, CheckCircle, AlertCircle, Heart, ExternalLink, Tag,
  ShoppingBag, Send, ChevronDown, Sparkles, Shield
} from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

const RARITY_STYLES = {
  common: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300', gradient: 'from-gray-200 to-gray-400' },
  uncommon: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', gradient: 'from-green-300 to-emerald-500' },
  rare: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', gradient: 'from-blue-300 to-blue-600' },
  epic: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300', gradient: 'from-purple-400 to-purple-700' },
  legendary: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', gradient: 'from-amber-300 to-orange-600' },
};

const NFTMarketplace = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('browse');
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [myCollectibles, setMyCollectibles] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState('newest');
  const [actionLoading, setActionLoading] = useState(null);

  // List for sale form
  const [showListForm, setShowListForm] = useState(false);
  const [listCollectible, setListCollectible] = useState('');
  const [listPrice, setListPrice] = useState(50);
  const [listDesc, setListDesc] = useState('');

  // Transfer form
  const [showTransfer, setShowTransfer] = useState(null);
  const [transferEmail, setTransferEmail] = useState('');

  // Feb 2026 — deep-link highlight. Notifications fired when a teen's
  // idea earns a Voice-of-HiBFF NFT use the URL `?highlight=<id>`.
  // We switch to the "My Vault" tab, scroll the matching card into
  // view, and pulse it for 3 seconds so it's impossible to miss.
  const location = useLocation();
  const navigate = useNavigate();
  const [highlightId, setHighlightId] = useState('');
  const highlightedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const h = params.get('highlight');
    if (!h) return;
    setHighlightId(h);
    setTab('my-nfts'); // switch to the user's vault tab
  }, [location.search]);

  // Once the highlighted card is mounted, scroll it into view and
  // auto-clear the highlight after 3 seconds (and strip ?highlight from
  // the URL so a refresh doesn't keep pulsing).
  useEffect(() => {
    if (!highlightId || highlightedRef.current) return undefined;
    if (myCollectibles.length === 0) return undefined;
    // Wait one render tick for the DOM node to exist.
    const t1 = setTimeout(() => {
      const el = document.querySelector(`[data-collectible-id="${highlightId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        highlightedRef.current = true;
      }
    }, 250);
    const t2 = setTimeout(() => {
      setHighlightId('');
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('highlight');
        navigate(`${url.pathname}${url.search}`, { replace: true });
      } catch { /* navigate is best-effort */ void 0; }
    }, 3500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [highlightId, myCollectibles, navigate]);

  const loadWallet = useCallback(async () => {
    setWalletLoading(true);
    try {
      const w = await apiClient.request('/api/nft/wallet');
      setWallet(w);
      setMyCollectibles(w.collectibles || []);
    } catch { setWallet(null); }
    setWalletLoading(false);
  }, []);

  const loadListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort });
      if (filter) params.set('rarity', filter);
      const data = await apiClient.request(`/api/nft/marketplace?${params}`);
      setListings(data.listings || []);
    } catch { setListings([]); }
    setLoading(false);
  }, [filter, sort]);

  const loadStats = useCallback(async () => {
    try {
      const s = await apiClient.request('/api/nft/stats');
      setStats(s);
    } catch {}
  }, []);

  const loadTransactions = useCallback(async () => {
    try {
      const t = await apiClient.request('/api/nft/transactions');
      setTransactions(t.transactions || []);
    } catch {}
  }, []);

  useEffect(() => { loadWallet(); loadStats(); }, [loadWallet, loadStats]);
  useEffect(() => { if (tab === 'browse') loadListings(); }, [tab, loadListings]);
  useEffect(() => { if (tab === 'history') loadTransactions(); }, [tab, loadTransactions]);

  const createWallet = async () => {
    setActionLoading('wallet');
    try {
      const result = await apiClient.request('/api/nft/wallet/create', { method: 'POST' });
      setMessage({ type: 'success', text: `Wallet created: ${result.wallet_address}` });
      loadWallet();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Wallet creation failed' });
    }
    setActionLoading(null);
  };

  const mintOnChain = async (collectibleId) => {
    setActionLoading(collectibleId);
    try {
      const result = await apiClient.request('/api/nft/mint', {
        method: 'POST',
        body: JSON.stringify({ collectible_id: collectibleId }),
      });
      setMessage({ type: 'success', text: result.message });
      loadWallet();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Minting failed' });
    }
    setActionLoading(null);
  };

  const listForSale = async () => {
    if (!listCollectible || listPrice < 5) return;
    setActionLoading('listing');
    try {
      const result = await apiClient.request('/api/nft/marketplace/list', {
        method: 'POST',
        body: JSON.stringify({ collectible_id: listCollectible, price_hearts: listPrice, description: listDesc }),
      });
      setMessage({ type: 'success', text: result.message });
      setShowListForm(false);
      setListCollectible(''); setListPrice(50); setListDesc('');
      loadListings(); loadWallet();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Listing failed' });
    }
    setActionLoading(null);
  };

  const buyListing = async (listingId) => {
    setActionLoading(listingId);
    try {
      const result = await apiClient.request('/api/nft/marketplace/buy', {
        method: 'POST',
        body: JSON.stringify({ listing_id: listingId }),
      });
      setMessage({ type: 'success', text: result.message });
      loadListings(); loadWallet(); loadStats();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Purchase failed' });
    }
    setActionLoading(null);
  };

  const transferCollectible = async (collectibleId) => {
    if (!transferEmail) return;
    setActionLoading(collectibleId);
    try {
      const result = await apiClient.request('/api/nft/transfer', {
        method: 'POST',
        body: JSON.stringify({ collectible_id: collectibleId, recipient_email: transferEmail }),
      });
      setMessage({ type: 'success', text: result.message });
      setShowTransfer(null); setTransferEmail('');
      loadWallet();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Transfer failed' });
    }
    setActionLoading(null);
  };

  const heartsBalance = user?.hearts_balance || 0;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="nft-marketplace-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">NFT Marketplace</h1>
          <p className="text-gray-600">Trade, mint, and collect on-chain NFTs powered by Polygon</p>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="marketplace-stats">
            <div className="p-3 bg-white rounded-xl border border-gray-200 text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.active_listings}</p>
              <p className="text-xs text-gray-500">Active Listings</p>
            </div>
            <div className="p-3 bg-white rounded-xl border border-gray-200 text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.total_sold}</p>
              <p className="text-xs text-gray-500">Total Sold</p>
            </div>
            <div className="p-3 bg-white rounded-xl border border-gray-200 text-center">
              <p className="text-2xl font-bold text-purple-600">{stats.total_volume_hearts?.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Volume (Hearts)</p>
            </div>
            <div className="p-3 bg-white rounded-xl border border-gray-200 text-center">
              <p className="text-2xl font-bold text-pink-600">{heartsBalance.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Your Hearts</p>
            </div>
          </div>
        )}

        {/* Message */}
        {message && (
          <div className={`mb-4 p-3 rounded-xl flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid="marketplace-message">
            {message.type === 'success' ? <CheckCircle size={16} className="text-green-600" /> : <AlertCircle size={16} className="text-red-600" />}
            <p className={`text-sm ${message.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>{message.text}</p>
            <button onClick={() => setMessage(null)} className="ml-auto text-gray-400">&times;</button>
          </div>
        )}

        {/* Wallet Banner */}
        <div className="mb-6 p-4 sm:p-5 bg-gradient-to-r from-indigo-900 to-purple-900 rounded-2xl text-white" data-testid="wallet-banner">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/10 rounded-xl">
                <Wallet size={24} />
              </div>
              <div>
                <p className="text-sm text-purple-200">Your Wallet</p>
                {walletLoading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : wallet?.wallet ? (
                  <p className="font-mono text-sm">{wallet.wallet.wallet_address?.slice(0, 16)}...</p>
                ) : (
                  <p className="text-sm text-purple-300">No wallet yet</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm text-purple-200">Collectibles</p>
                <p className="text-xl font-bold">{wallet?.total_collectibles || 0}</p>
              </div>
              {!wallet?.wallet && (
                <button
                  onClick={createWallet}
                  disabled={actionLoading === 'wallet'}
                  className="px-4 py-2 bg-white text-purple-900 font-semibold rounded-lg hover:bg-purple-50 transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="create-wallet-btn"
                >
                  {actionLoading === 'wallet' ? <Loader2 size={14} className="animate-spin" /> : <Wallet size={14} />}
                  Create Wallet
                </button>
              )}
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-purple-300">
            <Shield size={12} /> Powered by Crossmint on Polygon &mdash; No seed phrases, no gas fees, just your email.
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl" data-testid="marketplace-tabs">
          {[
            { id: 'browse', label: 'Browse', icon: ShoppingBag },
            { id: 'my-nfts', label: 'My Collection', icon: Package },
            { id: 'sell', label: 'Sell / List', icon: Tag },
            { id: 'history', label: 'History', icon: TrendingUp },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon size={16} className="hidden sm:block" /> {t.label}
            </button>
          ))}
        </div>

        {/* ─── BROWSE TAB ─────────────────────────────────────────────────── */}
        {tab === 'browse' && (
          <div>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
              <div className="flex flex-wrap gap-1.5">
                {['', 'common', 'uncommon', 'rare', 'epic', 'legendary'].map(r => (
                  <button key={r} onClick={() => setFilter(r)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${filter === r ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                    data-testid={`filter-rarity-${r || 'all'}`}
                  >
                    {r || 'All'}
                  </button>
                ))}
              </div>
              <select value={sort} onChange={e => setSort(e.target.value)}
                className="ml-auto px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm"
                data-testid="sort-select"
              >
                <option value="newest">Newest</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
              </select>
            </div>

            {loading ? (
              <div className="py-12 text-center"><Loader2 size={32} className="animate-spin text-purple-500 mx-auto" /></div>
            ) : listings.length === 0 ? (
              <div className="py-12 text-center bg-white rounded-2xl border border-gray-200">
                <Package size={48} className="mx-auto text-gray-200 mb-3" />
                <p className="text-gray-500 font-medium">No listings found</p>
                <p className="text-sm text-gray-400 mt-1">Be the first to list a collectible!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {listings.map(l => {
                  const rs = RARITY_STYLES[l.collectible_rarity] || RARITY_STYLES.common;
                  return (
                    <div key={l.listing_id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow" data-testid={`listing-${l.listing_id}`}>
                      <div className={`h-3 bg-gradient-to-r ${rs.gradient}`} />
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-bold text-gray-900">{l.collectible_title}</h3>
                            <p className="text-xs text-gray-400">by {l.seller_name}</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${rs.bg} ${rs.text}`}>
                            {l.collectible_rarity}
                          </span>
                        </div>
                        {l.description && <p className="text-sm text-gray-600 mb-3">{l.description}</p>}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <Heart size={16} className="text-pink-500" fill="currentColor" />
                            <span className="text-lg font-bold text-gray-900">{l.price_hearts}</span>
                            <span className="text-xs text-gray-400">Hearts</span>
                          </div>
                          {l.is_onchain && (
                            <span className="flex items-center gap-1 text-[10px] text-purple-600 font-medium bg-purple-50 px-2 py-0.5 rounded-full">
                              <Sparkles size={10} /> On-chain
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => buyListing(l.listing_id)}
                          disabled={actionLoading === l.listing_id || l.seller_id === user?.user_id}
                          className="mt-3 w-full py-2.5 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                          data-testid={`buy-${l.listing_id}`}
                        >
                          {actionLoading === l.listing_id ? <Loader2 size={14} className="animate-spin" /> : <ShoppingBag size={14} />}
                          {l.seller_id === user?.user_id ? 'Your Listing' : 'Buy Now'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ─── MY COLLECTION TAB ──────────────────────────────────────────── */}
        {tab === 'my-nfts' && (
          <div>
            {myCollectibles.length === 0 ? (
              <div className="py-12 text-center bg-white rounded-2xl border border-gray-200">
                <Package size={48} className="mx-auto text-gray-200 mb-3" />
                <p className="text-gray-500 font-medium">No collectibles yet</p>
                <p className="text-sm text-gray-400 mt-1">Earn collectibles through challenges, streaks, and referrals!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {myCollectibles.map(c => {
                  const rs = RARITY_STYLES[c.rarity] || RARITY_STYLES.common;
                  const isMinted = c.mint_status === 'minted' || c.mint_status === 'internal';
                  return (
                    <div
                      key={c.collectible_id}
                      className={`bg-white rounded-2xl border overflow-hidden transition-all duration-500 ${
                        highlightId === c.collectible_id
                          ? 'border-purple-400 shadow-[0_0_0_4px_rgba(123,79,216,0.35)] ring-4 ring-purple-300 animate-pulse'
                          : 'border-gray-200'
                      }`}
                      data-testid={`collectible-${c.collectible_id}`}
                      data-collectible-id={c.collectible_id}
                    >
                      <div className={`h-2 bg-gradient-to-r ${rs.gradient}`} />
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-bold text-gray-900">{c.title}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${rs.bg} ${rs.text}`}>
                            {c.rarity}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mb-3">{c.description?.slice(0, 80)}</p>
                        <div className="flex items-center gap-2 mb-3">
                          {c.mint_status === 'minted' ? (
                            <span className="flex items-center gap-1 text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">
                              <CheckCircle size={10} /> Minted on Polygon
                            </span>
                          ) : c.mint_status === 'pending' ? (
                            <span className="flex items-center gap-1 text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded-full">
                              <Loader2 size={10} className="animate-spin" /> Minting...
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">In-app only</span>
                          )}
                          {c.is_tradeable && <span className="text-xs text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full">Tradeable</span>}
                        </div>
                        <div className="flex gap-2">
                          {!isMinted && (
                            <button
                              onClick={() => mintOnChain(c.collectible_id)}
                              disabled={actionLoading === c.collectible_id}
                              className="flex-1 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-1"
                              data-testid={`mint-${c.collectible_id}`}
                            >
                              {actionLoading === c.collectible_id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                              Mint NFT
                            </button>
                          )}
                          {(c.mint_status === 'minted' && c.nft_contract && c.nft_token_id) && (
                            <a
                              href={`https://opensea.io/assets/matic/${c.nft_contract}/${c.nft_token_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 flex items-center justify-center gap-1.5"
                              data-testid={`opensea-${c.collectible_id}`}
                            >
                              <ExternalLink size={12} /> View on OpenSea
                            </a>
                          )}
                          {(c.mint_status === 'minted' && !(c.nft_contract && c.nft_token_id)) && (
                            <a
                              href="https://opensea.io/account"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 flex items-center justify-center gap-1.5"
                              data-testid={`opensea-${c.collectible_id}`}
                            >
                              <ExternalLink size={12} /> View on OpenSea
                            </a>
                          )}
                          <button
                            onClick={() => setShowTransfer(showTransfer === c.collectible_id ? null : c.collectible_id)}
                            className="px-3 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50"
                            data-testid={`transfer-btn-${c.collectible_id}`}
                          >
                            <Send size={14} />
                          </button>
                        </div>
                        {showTransfer === c.collectible_id && (
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid={`transfer-form-${c.collectible_id}`}>
                            <p className="text-xs text-gray-600 mb-2">Transfer to another user:</p>
                            <div className="flex gap-2">
                              <input
                                type="email" value={transferEmail}
                                onChange={e => setTransferEmail(e.target.value)}
                                placeholder="Recipient's email"
                                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                data-testid={`transfer-email-${c.collectible_id}`}
                              />
                              <button
                                onClick={() => transferCollectible(c.collectible_id)}
                                disabled={!transferEmail || actionLoading === c.collectible_id}
                                className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                data-testid={`transfer-confirm-${c.collectible_id}`}
                              >
                                Send
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ─── SELL TAB ───────────────────────────────────────────────────── */}
        {tab === 'sell' && (
          <div className="max-w-xl mx-auto">
            <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="sell-form">
              <div className="flex items-center gap-3 mb-5">
                <Tag size={24} className="text-purple-500" />
                <div>
                  <h2 className="text-xl font-bold text-gray-900">List for Sale</h2>
                  <p className="text-sm text-gray-600">Set your price in Hearts and list on the marketplace</p>
                </div>
              </div>

              {myCollectibles.filter(c => c.is_tradeable).length === 0 ? (
                <p className="text-gray-500 text-sm">No tradeable collectibles in your vault.</p>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Collectible</label>
                    <select value={listCollectible} onChange={e => setListCollectible(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                      data-testid="sell-select-collectible"
                    >
                      <option value="">Choose a collectible...</option>
                      {myCollectibles.filter(c => c.is_tradeable).map(c => (
                        <option key={c.collectible_id} value={c.collectible_id}>
                          {c.title} ({(c.rarity || 'common').toUpperCase()}) {c.mint_status === 'minted' ? '- On-chain' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price (Hearts)</label>
                    <input type="number" value={listPrice} onChange={e => setListPrice(Number(e.target.value))}
                      min={5} max={100000} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                      data-testid="sell-price"
                    />
                    <p className="text-xs text-gray-400 mt-1">Minimum: 5 Hearts, Maximum: 100,000 Hearts</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                    <input type="text" value={listDesc} onChange={e => setListDesc(e.target.value)}
                      placeholder="Why is this collectible special?"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                      data-testid="sell-description"
                    />
                  </div>
                  <button onClick={listForSale} disabled={!listCollectible || listPrice < 5 || actionLoading === 'listing'}
                    className="w-full py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="sell-confirm-btn"
                  >
                    {actionLoading === 'listing' ? <><Loader2 size={16} className="animate-spin" /> Listing...</> : <><Tag size={16} /> List for Sale</>}
                  </button>
                </div>
              )}
            </div>

            {/* How It Works */}
            <div className="mt-6 p-5 bg-gray-50 rounded-2xl border border-gray-200">
              <h3 className="font-bold text-gray-900 mb-3">How Trading Works</h3>
              <div className="space-y-3">
                {[
                  { step: '1', text: 'Earn collectibles through challenges, streaks, and referrals', icon: '🏆' },
                  { step: '2', text: 'Mint them as NFTs on Polygon (optional, for on-chain ownership)', icon: '⛓' },
                  { step: '3', text: 'Set your price in Hearts and list on the marketplace', icon: '🏷' },
                  { step: '4', text: 'When someone buys, Hearts transfer instantly. On-chain NFTs transfer on Polygon.', icon: '🤝' },
                ].map(s => (
                  <div key={s.step} className="flex items-start gap-3">
                    <span className="w-7 h-7 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{s.step}</span>
                    <p className="text-sm text-gray-600">{s.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ─── HISTORY TAB ────────────────────────────────────────────────── */}
        {tab === 'history' && (
          <div>
            {transactions.length === 0 ? (
              <div className="py-12 text-center bg-white rounded-2xl border border-gray-200">
                <ArrowLeftRight size={48} className="mx-auto text-gray-200 mb-3" />
                <p className="text-gray-500 font-medium">No transactions yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.map((t, i) => (
                  <div key={t.transaction_id || i} className="p-4 bg-white rounded-xl border border-gray-200 flex items-center gap-4" data-testid={`tx-${t.transaction_id}`}>
                    <div className={`p-2 rounded-lg ${t.type === 'sale' ? 'bg-green-100' : 'bg-blue-100'}`}>
                      {t.type === 'sale' ? <ShoppingBag size={18} className="text-green-600" /> : <Send size={18} className="text-blue-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">
                        {t.type === 'sale'
                          ? (t.buyer_id === user?.user_id ? 'Purchased' : 'Sold')
                          : (t.sender_id === user?.user_id ? 'Sent' : 'Received')
                        }
                      </p>
                      <p className="text-xs text-gray-400">{new Date(t.created_at).toLocaleDateString()}</p>
                    </div>
                    {t.price_hearts && (
                      <div className="flex items-center gap-1">
                        <Heart size={14} className="text-pink-500" fill="currentColor" />
                        <span className="font-bold text-gray-900">{t.price_hearts}</span>
                      </div>
                    )}
                    {t.is_onchain && (
                      <span className="text-[10px] text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full font-medium">On-chain</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default NFTMarketplace;
